// Run lifecycle state machine — pure logic extracted from Runs.tsx so the
// transition rules + their privilege guards are unit-testable (PACK-008…012).
//
//   ACTIVE ──Stop Tipping──▶ TIPPING_COMPLETE ──Complete Run──▶ COMPLETE ──Verify──▶ VERIFIED
//     │                            │                               │
//     └────────Complete Run────────┘                               │
//     ▲────Reopen (admin)──────────┘                               │
//     ▲──────────────────── Reopen (admin) ─────────────────────────┘
//
// Verify (COMPLETE → VERIFIED) is a SECURITY DEFINER RPC (verify_run) with its
// own pgTAP gate (verify_run_gate.sql, PACK-018) and is reached from the run
// detail / verify panel, not this list-view action set, so it's absent here.
//
// Both reopen paths run the SAME mutation (status → ACTIVE, clearing the
// tipping_stopped_at / completed_at stamps), so they share the 'reopen' kind;
// they differ only in label + confirm copy/tone (the COMPLETE reopen is
// higher-friction because verification / invoicing may already be downstream).

// Mirrors the DB enum run_status (001_schema + 035) PLUS the derived 'VERIFIED'
// the Runs register maps a verified_at onto. ARCHIVED is a real DB value the
// list can show (its filter chip is live) — it must not fall off the switch.
export type RunStatus = 'ACTIVE' | 'TIPPING_COMPLETE' | 'COMPLETE' | 'ARCHIVED' | 'VERIFIED'

/** The run statuses a run PICKER on a packing-side surface offers — packing,
 *  QC, rejects, downtime, piece-work capture: still open after tipping stops. The DB
 *  enum is ACTIVE | TIPPING_COMPLETE | COMPLETE | ARCHIVED (000_schema + 035);
 *  PackerCapture filtered on IN_PROGRESS / PENDING, which are not members, so
 *  PostgREST answered 22P02 and `data ?? []` rendered an empty picker — for
 *  every packhouse, silently, from the day it shipped. runStatus.enum.test.ts
 *  pins this list against the enum literals parsed out of the baseline SQL. */
export const PACKING_OPEN_STATUSES = ['ACTIVE', 'TIPPING_COMPLETE'] as const satisfies readonly RunStatus[]

export type RunActionKind = 'stopTipping' | 'completeRun' | 'reopen' | 'archive'

/** Presentational colour tone for the action button. */
export type RunActionTone = 'amber' | 'orange' | 'emerald'

export interface RunActionConfirm {
  /** `{run}` is replaced with the run number by the renderer. */
  title: string
  message: string
  confirm: string
  tone: 'warning' | 'danger'
}

export interface RunAction {
  kind: RunActionKind
  /** Resulting run.status after the transition. */
  to: RunStatus
  /** Button label. */
  label: string
  /** Recorded in runs.update_reason for the optimistic update. */
  reason: string
  /** Button colour tone. */
  tone: RunActionTone
  /** Confirm-dialog config, or null for immediate (no-confirm) actions. */
  confirm: RunActionConfirm | null
}

const STOP_TIPPING: RunAction = {
  kind: 'stopTipping', to: 'TIPPING_COMPLETE', label: 'Stop Tipping',
  reason: 'stop tipping (manual)', tone: 'amber', confirm: null,
}

const COMPLETE_RUN: RunAction = {
  kind: 'completeRun', to: 'COMPLETE', label: 'Complete Run',
  reason: 'complete run', tone: 'orange', confirm: null,
}

// R1b: a run made in error. The caller offers it only for a run with nothing
// tipped and no pallets (canArchive); the hook asks for a reason and re-checks
// both counts against the server before it writes ARCHIVED.
const ARCHIVE_RUN: RunAction = {
  kind: 'archive', to: 'ARCHIVED', label: 'Archive run',
  reason: 'archive run', tone: 'amber', confirm: null,
}

/** R1b: a run that may be archived: nothing tipped, no pallets, still open. Counts of 0 only: null/undefined never qualify. */
export function canArchiveRun(r: { status: string; tipped_net_weight_kg?: number | null; pallet_count?: number | null; run_kind?: string | null }): boolean {
  return (r.status === 'ACTIVE' || r.status === 'TIPPING_COMPLETE')
    && r.run_kind !== 'EXTERNAL_RECEIPT'
    && r.tipped_net_weight_kg === 0
    && r.pallet_count === 0
}

// Reopen from TIPPING_COMPLETE — lower friction (warning): no downstream work yet.
const REOPEN_TIPPING: RunAction = {
  kind: 'reopen', to: 'ACTIVE', label: 'Reopen for Tipping',
  reason: 'reopen for tipping', tone: 'emerald',
  confirm: {
    title: 'Reopen {run} for tipping?',
    message: 'Status returns to ACTIVE so deliveries can be tipped onto this run again. Use when Stop Tipping was clicked by mistake.',
    confirm: 'Reopen for tipping',
    tone: 'warning',
  },
}

// Reopen from COMPLETE — higher friction (danger): verification / invoicing
// downstream of COMPLETE may already have kicked in and need re-running.
const REOPEN_RUN: RunAction = {
  kind: 'reopen', to: 'ACTIVE', label: 'Reopen Run (admin)',
  reason: 'reopen for tipping', tone: 'emerald',
  confirm: {
    title: 'Reopen {run}?',
    message: 'Run was marked COMPLETE. Reopening sets status back to ACTIVE so it accepts new tipping / packing. Any verification / invoice work downstream may need to be re-run. Use only for closed-by-mistake cases.',
    confirm: 'Reopen',
    tone: 'danger',
  },
}

// Forward state machine for the run-list action panel: given a run's current
// status and whether the viewer is an admin, the operator transitions available.
// An empty list = no panel actions (VERIFIED, or COMPLETE for a non-admin).
// Reopen from COMPLETE is admin-only; reopen from TIPPING_COMPLETE is admin or
// manager (UF-17) — the privilege boundary an operator can't cross from the list view.
export function runActions(status: RunStatus, opts: { isAdmin: boolean; isManager?: boolean; canArchive?: boolean }): RunAction[] {
  const { isAdmin } = opts
  // UF-17: a manager may also undo Stop Tipping (the runs_sensitive_col_gate
  // trigger lets MANAGER change status). Reopening a COMPLETE run stays admin-only.
  const canReopenTipping = isAdmin || opts.isManager === true
  // R1b: manager tier or above (the gate trigger lets MANAGER change status).
  const withArchive = (a: RunAction[]): RunAction[] =>
    opts.canArchive === true && canReopenTipping ? [...a, ARCHIVE_RUN] : a
  switch (status) {
    case 'ACTIVE':
      return withArchive([STOP_TIPPING, COMPLETE_RUN])
    case 'TIPPING_COMPLETE':
      return withArchive(canReopenTipping ? [COMPLETE_RUN, REOPEN_TIPPING] : [COMPLETE_RUN])
    case 'COMPLETE':
      return isAdmin ? [REOPEN_RUN] : []
    case 'ARCHIVED':
    case 'VERIFIED':
      return []
    default:
      // An unknown status renders no panel actions rather than throwing on
      // `.length` in RunActionButtons — a new DB value must never blank the
      // whole register.
      return []
  }
}
